<template>
  <div
    id="avatar_button"
    :class="{ 'dark-mode': darkMode, }"
  >
    <ButtonPopover
      v-model="isPopoverOpen"
      :color="darkMode ? 'white' : 'primary'"
      icon="both"
      placement="bottom-right"
      trigger="click"
    >
      <template #left-icon>
        <Avatar
          :notification="notifications.count > 0 ? notifications.count : -1"
          :ext-img="user.avatar"
          :img-name="user.avatar_from_library"
        />
      </template>
      <template #label>
        <div class="name-container">
          <span
            v-if="user"
            class="text-small"
          >
            {{ user.contact.prenom }}
          </span>
          <span
            v-if="user"
            class="text-small uppercase"
          >
            {{ user.contact.nom }}
          </span>
        </div>
      </template>
      <template #right-icon>
        <UilAngleDown />
      </template>

      <ul
        v-if="infosUser.length"
        id="infos_user"
        class="text-medium"
      >
        <li
          v-for="info in infosUser"
          :key="info.label"
          :class="{ hidden: !info.value, }"
        >
          <span>{{ info.label }}</span>
          <span>{{ info.value }}</span>
        </li>
      </ul>

      <ul class="avatar-options">
        <li class="avatar-option">
          <button
            class="button option-btn disabled"
            @click="redirectToCompte()"
          >
            <span class="text text-medium">
              {{ $t('action.gerer-profil') }}
            </span>
            <UilUser size="16" />
          </button>
        </li>

        <!-- @slot Emplacement pour le menu déroulant du popover. -->
        <slot name="options" />
      </ul>

      <!-- Compartiment des notifications -->
      <div
        class="notifications-container"
        :class="{ empty: notifications.count === 0, }"
      >
        <div class="notifications-header">
          <strong class="text-regular notifications-title">
            {{ $t('compte.x-notification', notifications.count, { nb: notifications.count, }) }}
          </strong>
          <button
            v-if="notifications.count > 0"
            class="option-btn delete-notifications-btn"
            @click="$emit('clear-notifications')"
          >
            <span class="text">{{ $t('action.vider-notifications') }}</span>
            <UilBellSlash size="16" />
          </button>
        </div>
        <!-- Notifications container -->
        <div
          v-if="notifications.count"
          class="notifications-content"
        >
          <ul>
            <li
              v-for="notification in notifications.results"
              :key="notification.id"
            >
              <Notification
                :notification="notification"
                @read="$emit('read', $event); close()"
              />
            </li>
          </ul>
        </div>
      </div>

      <!-- @slot Emplacement pour d'autres éléments. -->
      <slot name="other" />

      <button
        class="option-btn"
        @click="$emit('disconnect')"
      >
        <span class="text text-medium">{{ $t('action.me-deconnecter') }}</span>
        <UilSignout size="16" />
      </button>
    </ButtonPopover>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import ButtonPopover from "../../components/buttons/ButtonPopover.vue";
import Avatar from "../../components/avatar/Avatar.vue";
import Notification from "../../components/infos/Notification.vue";

import {
  UilAngleDown,
  UilUser,
  UilBellSlash,
  UilSignout,
} from "@lde/vue-unicons";

/**
 * Affiche l'avatar avec un menu déroulant pour l'utilisateur.
 */
export default defineComponent({
  name: "AvatarButton",
  components: {
    Avatar,
    Notification,
    ButtonPopover,
    UilAngleDown,
    UilUser,
    UilBellSlash,
    UilSignout,
  },
  props: {
    /**
     * Définit l'utilisateur courant.
     */
    user: {
      type: Object,
      required: true,
    },
    /**
     * Informations de l'utilisateur affichées dans le popover de l'avatar.
     */
    infosUser: {
      type: Array,
      default: () => [],
    },
    /**
     * Définit les notifications de l'utilisateur.
     */
    notifications: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Définit si le mode nuit est activé.
     */
    darkMode: {
      type: Boolean,
      default: false,
    },
    /**
     * Définit si le popover est ouvert.
     */
    modelValue: {
      type: Boolean,
      default: true,
    },
  },
  emits: [
    /**
     * Déclenché au clic sur le bouton de suppression des notifications.
     */
    "clear-notifications",
    /**
     * Déclenché au clic sur le bouton de déconnexion.
     */
    "disconnect",
    /**
     * Déclenché au clic sur la notification.
     * @param {Object} - Notification ciblée.
     */
    "read",
    /**
     * Déclenché à l'ouverture et à la fermeture du popover.
     */
    "update:modelValue",
  ],
  computed: {
    isPopoverOpen: {
      get() {
        return this.modelValue;
      },
      set(newV) {
        this.$emit("update:modelValue", newV);
      },
    },
  },
  methods: {
    /**
     * Ferme le popover.
     */
    close() {
      this.$emit("update:modelValue", false);
    },
    /**
     * Redirige vers la page de compte.
     */
    redirectToCompte() {
      const name = "compte";
      if (this.$route.name !== name) {
        this.$router.push({ name });
      }
      this.close();
    },
  },
});
</script>

<style lang="scss">
@use "../../assets/styles/components/avatar/avatar_button.scss";
</style>
